
import { useState, useEffect } from 'react';

const CalendarWidget = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [allEvents] = useState([
    {
      id: 1,
      title: 'Project Kickoff Meeting',
      date: '2024-01-25',
      time: '10:00 AM',
      type: 'meeting',
      location: 'Conference Room A',
      attendees: ['John Doe', 'Sarah Wilson']
    },
    {
      id: 2,
      title: 'Website Redesign Deadline',
      date: '2024-01-28',
      time: '5:00 PM',
      type: 'deadline',
      project: 'TechCorp Website',
      priority: 'High'
    },
    {
      id: 3,
      title: 'Client Presentation',
      date: '2024-01-30',
      time: '2:00 PM',
      type: 'presentation',
      client: 'Johnson Family',
      project: 'Sunset Villa Renovation'
    }
  ]);
  const [filteredEvents, setFilteredEvents] = useState(allEvents);

  // Load meetings from localStorage and merge with default events
  useEffect(() => {
    try {
      const storedMeetings = JSON.parse(localStorage.getItem('teamsdayMeetings') || '[]');
      const formattedMeetings = storedMeetings.map((meeting: any) => ({
        id: meeting.id,
        title: meeting.title,
        date: meeting.date,
        time: meeting.time,
        type: meeting.type,
        location: meeting.location,
        client: meeting.client
      }));
      
      // Merge and filter events based on selected date
      const mergedEvents = [...allEvents, ...formattedMeetings];
      filterEventsByDate(selectedDate, mergedEvents);
    } catch (error) {
      console.error('Error loading meetings from localStorage:', error);
      filterEventsByDate(selectedDate, allEvents);
    }
  }, []);

  // Filter events when date changes
  const filterEventsByDate = (date: Date, eventsToFilter?: any[]) => {
    const selectedDateString = date.toISOString().split('T')[0];
    const events = eventsToFilter || [...allEvents];
    
    // Get events on the selected date (changed from >= to === for better UX)
    const filtered = events.filter(event => {
      return event.date === selectedDateString;
    });
    
    // Sort by time instead of date since we're showing events for a single day
    filtered.sort((a, b) => a.time.localeCompare(b.time));
    
    setFilteredEvents(filtered);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleDateSelect = (day: number) => {
    const newSelectedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(newSelectedDate);
    
    // Filter events based on selected date
    try {
      const storedMeetings = JSON.parse(localStorage.getItem('teamsdayMeetings') || '[]');
      const formattedMeetings = storedMeetings.map((meeting: any) => ({
        id: meeting.id,
        title: meeting.title,
        date: meeting.date,
        time: meeting.time,
        type: meeting.type,
        location: meeting.location,
        client: meeting.client
      }));
      
      const mergedEvents = [...allEvents, ...formattedMeetings];
      filterEventsByDate(newSelectedDate, mergedEvents);
    } catch (error) {
      console.error('Error loading meetings from localStorage:', error);
      filterEventsByDate(newSelectedDate, allEvents);
    }
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = 
        day === new Date().getDate() &&
        currentDate.getMonth() === new Date().getMonth() &&
        currentDate.getFullYear() === new Date().getFullYear();
      
      const isSelected = 
        day === selectedDate.getDate() &&
        currentDate.getMonth() === selectedDate.getMonth() &&
        currentDate.getFullYear() === selectedDate.getFullYear();

      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(day)}
          className={`h-8 w-8 flex items-center justify-center text-sm rounded-full transition-colors cursor-pointer ${
            isToday
              ? 'bg-blue-600 text-white'
              : isSelected
              ? 'bg-blue-100 text-blue-600'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const handleEventClick = (event: any) => {
    // Navigate directly to calendar page with event details
    if (typeof window !== 'undefined' && (window as any).REACT_APP_NAVIGATE) {
      try {
        (window as any).REACT_APP_NAVIGATE(`/calendar?event=${event.id}&date=${event.date}`);
      } catch (error) {
        console.error('Navigation error:', error);
      }
    }
  };

  const handleViewAllEvents = () => {
    if (typeof window !== 'undefined' && (window as any).REACT_APP_NAVIGATE) {
      try {
        (window as any).REACT_APP_NAVIGATE('/calendar');
      } catch (error) {
        console.error('Navigation error:', error);
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Calendar</h3>
          <button
            onClick={handleViewAllEvents}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
          >
            View All
          </button>
        </div>
      </div>

      <div className="p-4">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-900">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h4>
          <div className="flex space-x-1">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-1 rounded hover:bg-gray-100"
            >
              <i className="ri-arrow-left-s-line text-gray-600"></i>
            </button>
            <button
              onClick={() => navigateMonth('next')}
              className="p-1 rounded hover:bg-gray-100"
            >
              <i className="ri-arrow-right-s-line text-gray-600"></i>
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day) => (
            <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-gray-500">
              {day}
            </div>
          ))}
          {renderCalendarDays()}
        </div>

        {/* Selected Date Display */}
        <div className="mb-4 p-2 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            Selected: {selectedDate.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </p>
        </div>

        {/* Upcoming Events */}
        <div>
          <h5 className="text-sm font-medium text-gray-900 mb-3">
            Events for {filteredEvents.length > 0 && `(${filteredEvents.length})`}
          </h5>
          <div className="space-y-2">
            {filteredEvents.length > 0 ? (
              filteredEvents.map((event) => (
                <div 
                  key={event.id} 
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleEventClick(event)}
                >
                  <div className={`w-2 h-2 rounded-full ${
                    event.type === 'meeting' ? 'bg-blue-500' :
                    event.type === 'deadline' ? 'bg-red-500' : 'bg-green-500'
                  }`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{event.title}</p>
                    <p className="text-xs text-gray-500">{event.time}</p>
                    {event.location && (
                      <p className="text-xs text-gray-500">{event.location}</p>
                    )}
                    {event.project && (
                      <p className="text-xs text-blue-600">{event.project}</p>
                    )}
                    {event.client && (
                      <p className="text-xs text-gray-600">{event.client}</p>
                    )}
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-400 text-sm"></i>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <i className="ri-calendar-line text-2xl text-gray-300 mb-2"></i>
                <p className="text-sm text-gray-500">No events on this date</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarWidget;
